import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { getMeasurementLabel } from '@/utils/measurementTranslations';
import { Globe } from 'lucide-react';

export interface MeasurementData {
  // Upper Body Measurements (in inches)
  neck?: number | string;
  shoulder?: number | string;
  shoulder_width?: number | string;  // Alias for shoulder
  chest?: number | string;
  upper_waist?: number | string;
  upper_hip?: number | string;
  bicep?: number | string;
  forearm?: number | string;
  wrist?: number | string;
  sleeve_length?: number | string;
  sleeve_width?: number | string;
  armhole?: number | string;
  upper_length?: number | string;
  front_length?: number | string;
  center_back?: number | string;
  back_length?: number | string;
  
  // Lower Body Measurements (in inches)
  waist?: number | string;
  hips?: number | string;
  thigh?: number | string;
  knee?: number | string;
  calf?: number | string;
  ankle?: number | string;
  inseam?: number | string;
  outseam?: number | string;
  crotch?: number | string;
  pant_length?: number | string;
  bottom?: number | string;
  
  // Additional Measurements
  height?: number | string;
  weight?: number | string;
}

export interface MeasurementFormProps {
  /** Initial measurement values */
  values?: MeasurementData;
  /** Callback when measurement values change */
  onChange: (measurements: MeasurementData) => void;
  /** Custom className for the container */
  className?: string;
  /** Show language switcher (default: true) */
  showLanguageSwitcher?: boolean;
  /** Show section headers (default: true) */
  showSectionHeaders?: boolean;
  /** Layout variant: 'grid' (default) or 'compact' */
  layout?: 'grid' | 'compact';
  /** Fields to include (default: all) */
  fields?: string[];
  /** Fields to exclude */
  excludeFields?: string[];
}

type MeasurementFieldConfig = {
  name: keyof MeasurementData & string;
  placeholder: string;
  icon: string;
  labelKey?: string;
};

const MeasurementForm: React.FC<MeasurementFormProps> = ({
  values = {},
  onChange,
  className = '',
  showLanguageSwitcher = true,
  showSectionHeaders = true,
  layout = 'grid',
  fields,
  excludeFields = [],
}) => {
  const { t, currentLanguage, setLanguage, languages } = useLanguage();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const languageSwitcherRef = useRef<HTMLDivElement>(null);

  // Close language switcher when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageSwitcherRef.current && !languageSwitcherRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };

    if (showLanguageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLanguageDropdown]);

  const getMeasurementFieldLabel = (fieldName: string) => {
    return getMeasurementLabel(fieldName, t);
  };

  const upperBodyMeasurements: MeasurementFieldConfig[] = [
    { name: 'neck', placeholder: '15.5', icon: '👔', labelKey: 'measurement.concise.neck' },
    { name: 'shoulder_width', placeholder: '18', icon: '📏', labelKey: 'measurement.concise.shoulder' },
    { name: 'chest', placeholder: '40', icon: '📏', labelKey: 'measurement.concise.chest' },
    { name: 'upper_waist', placeholder: '34', icon: '📏', labelKey: 'measurement.concise.upper_waist' },
    { name: 'upper_hip', placeholder: '38', icon: '📏', labelKey: 'measurement.concise.upper_hip' },
    { name: 'sleeve_length', placeholder: '25', icon: '📏', labelKey: 'measurement.concise.sleeve' },
    { name: 'armhole', placeholder: '20', icon: '📏', labelKey: 'measurement.concise.armhole' },
    { name: 'bicep', placeholder: '13.5', icon: '💪', labelKey: 'measurement.concise.bicep' },
    { name: 'forearm', placeholder: '11.5', icon: '💪', labelKey: 'measurement.concise.forearm' },
    { name: 'wrist', placeholder: '7.5', icon: '📏', labelKey: 'measurement.concise.wrist' },
    { name: 'back_length', placeholder: '17', icon: '📏', labelKey: 'measurement.concise.back' },
    { name: 'upper_length', placeholder: '30', icon: '📏', labelKey: 'measurement.concise.top_length' }
  ];
  
  const lowerBodyMeasurements: MeasurementFieldConfig[] = [
    { name: 'waist', placeholder: '34', icon: '📏', labelKey: 'measurement.concise.waist' },
    { name: 'hips', placeholder: '40', icon: '📏', labelKey: 'measurement.concise.hip' },
    { name: 'thigh', placeholder: '23', icon: '💪', labelKey: 'measurement.concise.thigh' },
    { name: 'knee', placeholder: '16', icon: '📏', labelKey: 'measurement.concise.knee' },
    { name: 'calf', placeholder: '15', icon: '💪', labelKey: 'measurement.concise.calf' },
    { name: 'ankle', placeholder: '9', icon: '📏', labelKey: 'measurement.concise.bottom' },
    { name: 'crotch', placeholder: '11', icon: '📏', labelKey: 'measurement.concise.crotch' },
    { name: 'inseam', placeholder: '31', icon: '📏', labelKey: 'measurement.concise.inseam' },
    { name: 'pant_length', placeholder: '40', icon: '📏', labelKey: 'measurement.concise.length' }
  ];

  const additionalMeasurements: MeasurementFieldConfig[] = [
    { name: 'height', placeholder: '67', icon: '📏', labelKey: 'measurement.concise.height' },
    { name: 'weight', placeholder: '154', icon: '⚖️', labelKey: 'measurement.concise.weight' }
  ];

  // Filter fields based on props
  const shouldIncludeField = (fieldName: string) => {
    if (excludeFields.includes(fieldName)) return false;
    if (fields && fields.length > 0) return fields.includes(fieldName);
    return true;
  };

  const filteredUpperBody = upperBodyMeasurements.filter(f => shouldIncludeField(f.name));
  const filteredLowerBody = lowerBodyMeasurements.filter(f => shouldIncludeField(f.name));
  const filteredAdditional = additionalMeasurements.filter(f => shouldIncludeField(f.name));

  const handleFieldChange = (fieldName: string, value: string) => {
    const numValue = value === '' ? '' : parseFloat(value) || 0;
    onChange({
      ...values,
      [fieldName]: numValue
    });
  };

  const renderMeasurementInput = (field: MeasurementFieldConfig) => {
    const value = values[field.name as keyof MeasurementData];
    const displayValue = value === 0 || value === '' || value === null || value === undefined ? '' : String(value);
    
    // Handle spinner clicks - initialize with placeholder if empty, then increment/decrement
    const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      const rect = target.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const isSpinnerClick = clickX > rect.width - 30; // Spinner buttons are typically ~30px wide
      
      if (isSpinnerClick && (value === 0 || value === '' || value === null || value === undefined)) {
        e.preventDefault();
        const placeholderValue = parseFloat(field.placeholder) || 0;
        
        // Determine if clicking up or down button based on Y position
        const clickY = e.clientY - rect.top;
        const isUpButton = clickY < rect.height / 2;
        
        if (isUpButton) {
          // Increment from placeholder
          handleFieldChange(field.name, String(placeholderValue + 0.1));
        } else {
          // Decrement from placeholder (but not below 0)
          handleFieldChange(field.name, String(Math.max(0, placeholderValue - 0.1)));
        }
      }
    };
    
    // Handle up/down arrow key presses - initialize with placeholder if empty
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && 
          (value === 0 || value === '' || value === null || value === undefined)) {
        e.preventDefault();
        const placeholderValue = parseFloat(field.placeholder) || 0;
        if (e.key === 'ArrowUp') {
          handleFieldChange(field.name, String(placeholderValue + 0.1));
        } else {
          handleFieldChange(field.name, String(Math.max(0, placeholderValue - 0.1)));
        }
      }
    };
    
    return (
      <div key={field.name} className={layout === 'compact' ? 'flex items-center gap-2' : 'text-center'}>
        <label className={`block text-sm font-medium text-gray-700 mb-2 ${layout === 'compact' ? 'mb-0 min-w-[120px]' : 'whitespace-nowrap'}`}>
          {field.labelKey ? t(field.labelKey) : getMeasurementFieldLabel(field.name)}
        </label>
        <input
          type="number"
          value={displayValue}
          onChange={(e) => handleFieldChange(field.name, e.target.value)}
          onMouseDown={handleMouseDown}
          onKeyDown={handleKeyDown}
          step="0.1"
          min="0"
          className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg ${layout === 'compact' ? 'text-center' : 'text-center'}`}
          placeholder={field.placeholder}
        />
      </div>
    );
  };

  const LanguageSwitcher = () => {
    if (!showLanguageSwitcher) return null;

    return (
      <div className="relative" ref={languageSwitcherRef}>
        <button
          onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Change Language"
          type="button"
        >
          <Globe className="w-4 h-4" />
          <span>{languages.find(l => l.code === currentLanguage)?.flag || '🌐'}</span>
        </button>
        {showLanguageDropdown && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
            <div className="p-2">
              {languages.filter(l => ['en', 'hi', 'pa'].includes(l.code)).map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code);
                    setShowLanguageDropdown(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 transition-colors flex items-center space-x-2 ${
                    currentLanguage === lang.code ? 'bg-blue-50 text-blue-700' : ''
                  }`}
                  type="button"
                >
                  <span>{lang.flag}</span>
                  <span>{lang.nativeName}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={className}>
      {/* Upper Body Measurements */}
      {filteredUpperBody.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          {showSectionHeaders && (
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <span className="mr-2">👔</span>
                  {t('measurement.upper_body')}
                </h2>
                <span className="text-sm text-gray-500 font-normal">
                  ({t('measurement.inches')})
                </span>
              </div>
              <LanguageSwitcher />
            </div>
          )}
          {!showSectionHeaders && showLanguageSwitcher && (
            <div className="flex justify-end mb-4">
              <LanguageSwitcher />
            </div>
          )}
          {!showSectionHeaders && (
            <div className="text-sm text-gray-500 mb-4">
              {t('measurement.inches')}
            </div>
          )}
          <div className={layout === 'compact' 
            ? 'space-y-2' 
            : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4'}>
            {filteredUpperBody.map((field) => renderMeasurementInput(field))}
          </div>
        </div>
      )}

      {/* Lower Body Measurements */}
      {filteredLowerBody.length > 0 && (
        <div className={`bg-white rounded-lg shadow p-6 ${filteredUpperBody.length > 0 ? 'mt-6' : ''}`}>
          {showSectionHeaders && (
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <span className="mr-2">👖</span>
                {t('measurement.lower_body')}
              </h2>
              <span className="text-sm text-gray-500 font-normal">
                ({t('measurement.inches')})
              </span>
            </div>
          )}
          {!showSectionHeaders && (
            <div className="text-sm text-gray-500 mb-4">
              {t('measurement.inches')}
            </div>
          )}
          <div className={layout === 'compact' 
            ? 'space-y-2' 
            : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'}>
            {filteredLowerBody.map((field) => renderMeasurementInput(field))}
          </div>
        </div>
      )}

      {/* Additional Measurements */}
      {filteredAdditional.length > 0 && (
        <div className={`bg-white rounded-lg shadow p-6 ${filteredLowerBody.length > 0 || filteredUpperBody.length > 0 ? 'mt-6' : ''}`}>
          {showSectionHeaders && (
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <span className="mr-2">📏</span>
                {t('measurement.additional')}
              </h2>
              <span className="text-sm text-gray-500 font-normal">
                ({t('measurement.inches')} / lbs)
              </span>
            </div>
          )}
          {!showSectionHeaders && (
            <div className="text-sm text-gray-500 mb-4">
              {t('measurement.inches')} / lbs
            </div>
          )}
          <div className={layout === 'compact' 
            ? 'space-y-2' 
            : 'grid grid-cols-1 md:grid-cols-2 gap-6'}>
            {filteredAdditional.map((field) => renderMeasurementInput(field, field.name === 'weight'))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeasurementForm;

